import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { format } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { curingColour } from "../../api/curing";
import { useArchiveProject, usePinProject } from "../../api/projects";
import type { Project } from "../../types/project";
import { FROZEN_RIGHT, ROW_BG, frozenCellSx } from "./frozenColumns";
import { deriveProjectStatus } from "./projectStatus";

interface ProjectRowProps {
  project: Project;
  showFolder: boolean;
  folderLabel: string;
  highlighted: boolean;
  viewOnly: boolean;
  rowRef: (el: HTMLTableRowElement | null) => void;
  onHoverChange: (hovering: boolean) => void;
  onMoveToFolder: () => void;
  /** Fly the map to this project. */
  onLocate: () => void;
  /** Drawn on the map (its own flag). */
  mapVisible: boolean;
  /** Name of a folder above it that is hidden from the map, if any. */
  hiddenBy: string | null;
  onToggleMapVisible: () => void;
  /** Open the dialog that styles this project's property boundaries. */
  onStyleProperties: () => void;
  dragProps: React.HTMLAttributes<HTMLElement> & { draggable?: boolean };
  /** Listed inside the Archive: not openable; restore or delete instead of the usual actions. */
  archived?: boolean;
  onRestore?: () => void;
  onDeletePermanently?: () => void;
  canDeletePermanently?: boolean;
}

const TAGS_SHOWN = 2;

export default function ProjectRow({
  project,
  showFolder,
  folderLabel,
  highlighted,
  viewOnly,
  rowRef,
  onHoverChange,
  onMoveToFolder,
  onLocate,
  mapVisible,
  hiddenBy,
  onToggleMapVisible,
  onStyleProperties,
  dragProps,
  archived = false,
  onRestore,
  onDeletePermanently,
  canDeletePermanently = false,
}: ProjectRowProps) {
  const navigate = useNavigate();
  const { mutate: setPinned } = usePinProject();
  const { mutate: archiveProject, isPending: archivePending, isSuccess: archiveDone } = useArchiveProject();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const status = deriveProjectStatus(project);
  // Keep the archiving state through the post-success refetch so the row
  // stays visibly "archiving" until it unmounts.
  const isArchiving = archivePending || archiveDone;
  const isPinned = !!project.is_pinned;
  const openIncidents = project.open_incident_count ?? 0;
  const canOpen = !status.isBusy && !isArchiving && !archived;
  const hasShape = !!project.boundary || !!project.bbox;
  const tags = project.tags ?? [];
  const drawn = mapVisible && !hiddenBy;

  const open = () => {
    if (canOpen) navigate(`/projects/${project.id}`);
  };

  const counts = (value: number | undefined) => (status.isProcessing || status.isFailed ? "—" : value ?? 0);

  return (
    <TableRow
      ref={rowRef}
      onClick={open}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      {...dragProps}
      sx={{
        cursor: canOpen ? "pointer" : "default",
        opacity: status.isBusy || isArchiving ? 0.65 : 1,
        borderLeft: highlighted ? "3px solid #00d4ad" : "3px solid transparent",
        // Opaque colours: the frozen cells inherit them to cover scrolled content.
        backgroundColor: highlighted ? ROW_BG.highlightProject : ROW_BG.base,
        "&:hover": { backgroundColor: highlighted ? ROW_BG.highlightProjectHover : ROW_BG.hover },
        transition: "background-color 120ms ease",
      }}
    >
      <TableCell sx={{ minWidth: 200 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600 }}>
            {project.name}
          </Typography>
          {!hasShape && !status.isProcessing && (
            <Tooltip title="No map area yet">
              <LocationOffOutlinedIcon fontSize="small" sx={{ color: "#9ca3af" }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
          {archived ? (
            <Chip size="small" label="Archived" sx={{ height: 20, fontSize: "0.7rem" }} />
          ) : isArchiving ? (
            <Chip size="small" icon={<CircularProgress size={10} />} label="Archiving…" sx={{ height: 20, fontSize: "0.7rem" }} />
          ) : status.label ? (
            <Chip
              size="small"
              icon={status.isFailed ? <ErrorOutlineIcon /> : <CircularProgress size={10} />}
              label={status.label}
              color={status.isFailed ? "error" : "default"}
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          ) : status.backgroundLabel ? (
            <Tooltip title="Open it now. The rest of its data keeps loading in the background and appears on the map as it lands.">
              <Chip
                size="small"
                icon={<CheckCircleOutlineIcon />}
                label={`Ready to open · ${status.backgroundLabel.replace(/…$/, "")} in background`}
                color="success"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem", maxWidth: 360 }}
              />
            </Tooltip>
          ) : null}
          {openIncidents > 0 && !status.isFailed && (
            <Chip
              size="small"
              icon={<LocalFireDepartmentOutlinedIcon sx={{ fill: "#fff" }} />}
              label={`${openIncidents} open incident${openIncidents === 1 ? "" : "s"}`}
              sx={{
                height: 20,
                fontSize: "0.7rem",
                backgroundColor: "#f59e0b",
                color: "#fff",
                fontWeight: 600,
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
          )}
        </Box>
      </TableCell>
      {showFolder && (
        <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{folderLabel || "All projects"}</TableCell>
      )}
      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
        {project.updated ? format(new Date(project.updated), "MMM d, yyyy") : "—"}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{project.username}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {counts(project.strategy_count)}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {counts(project.fire_simulation_count)}
      </TableCell>
      <TableCell align="right">
        {project.curing_summary ? (
          <Tooltip title={`Grass curing measured from satellite imagery on ${project.curing_summary.estimate_date}`}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
              <GrassOutlinedIcon fontSize="small" sx={{ color: curingColour(project.curing_summary.mean_pct) }} />
              {Math.round(project.curing_summary.mean_pct)}%
            </Box>
          </Tooltip>
        ) : (
          <Typography component="span" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap" }}>
          {tags.slice(0, TAGS_SHOWN).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#e0f2fe", color: "#0369a1" }}
            />
          ))}
          {tags.length > TAGS_SHOWN && (
            <Tooltip title={tags.slice(TAGS_SHOWN).join(", ")}>
              <Chip
                label={`+${tags.length - TAGS_SHOWN}`}
                size="small"
                sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#f3f4f6", color: "#6b7280" }}
              />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {/* Frozen controls: these stay put while the columns above scroll. */}
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.pin)}>
        {!archived && (!viewOnly || isPinned) && (
          <Tooltip
            title={viewOnly ? "Pinned to the top for everyone" : isPinned ? "Unpin from top" : "Pin to top for everyone"}
            placement="left"
          >
            <span>
              <IconButton
                size="small"
                disabled={viewOnly || isArchiving}
                aria-label={isPinned ? "Unpin project" : "Pin project"}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinned({ projectId: String(project.id), pinned: !isPinned });
                }}
                sx={{
                  color: isPinned ? "#00d4ad" : "#9ca3af",
                  "&.Mui-disabled": { color: isPinned ? "#00d4ad" : "#d1d5db" },
                }}
              >
                {isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.eye)}>
        {!archived && (
          <Tooltip
            placement="left"
            title={
              hiddenBy
                ? `Hidden from the map because “${hiddenBy}” is hidden`
                : viewOnly
                  ? mapVisible
                    ? "Shown on the map"
                    : "Hidden from the map"
                  : mapVisible
                    ? "Hide from the map (for everyone)"
                    : "Show on the map (for everyone)"
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={viewOnly || !!hiddenBy}
                aria-label={mapVisible ? "Hide from the map" : "Show on the map"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMapVisible();
                }}
                sx={{
                  color: drawn ? "#64748b" : "#cbd5e1",
                  "&.Mui-disabled": { color: drawn ? "#64748b" : "#cbd5e1" },
                }}
              >
                {drawn ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.locate)}>
        {/* Tooltips open to the left: below would sit on the next row's button. */}
        <Tooltip title={hasShape ? "Show on map" : "No map area yet"} placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!hasShape}
              aria-label="Show on map"
              onClick={(e) => {
                e.stopPropagation();
                onLocate();
              }}
              sx={{ color: "#64748b", "&:hover": { color: "#00d4ad" } }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="none" align="right" sx={frozenCellSx(FROZEN_RIGHT.menu, true)}>
        <IconButton
          size="small"
          aria-label="Project actions"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onClick={(e) => e.stopPropagation()}
        >
          {archived && !viewOnly && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onRestore?.();
              }}
            >
              <RestoreOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Restore
            </MenuItem>
          )}
          {archived && canDeletePermanently && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onDeletePermanently?.();
              }}
            >
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete permanently
            </MenuItem>
          )}
          {archived && viewOnly && !canDeletePermanently && (
            <MenuItem disabled>Archived</MenuItem>
          )}
          {!archived && (
            <MenuItem
              disabled={!canOpen}
              onClick={() => {
                setMenuAnchor(null);
                open();
              }}
            >
              <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} /> Open
            </MenuItem>
          )}
          {!archived && !viewOnly && (
            <MenuItem
              disabled={isArchiving}
              onClick={() => {
                setMenuAnchor(null);
                setPinned({ projectId: String(project.id), pinned: !isPinned });
              }}
            >
              {isPinned ? <PushPinOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> : <PushPinIcon fontSize="small" sx={{ mr: 1 }} />}
              {isPinned ? "Unpin from top" : "Pin to top for everyone"}
            </MenuItem>
          )}
          {!archived && !viewOnly && (
            <MenuItem
              disabled={isArchiving}
              onClick={() => {
                setMenuAnchor(null);
                onMoveToFolder();
              }}
            >
              <DriveFileMoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Move to folder…
            </MenuItem>
          )}
          {!archived && !viewOnly && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onStyleProperties();
              }}
            >
              <PaletteOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Property boundary style…
            </MenuItem>
          )}
          {!archived && !viewOnly && (
            <MenuItem
              disabled={isArchiving}
              onClick={() => {
                setMenuAnchor(null);
                archiveProject(String(project.id));
              }}
            >
              <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Archive
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
}
